import type { Prescription } from "@/core/clinical/types";
import {
  canDeliverLine,
  canTransferLineToBilling,
} from "@/core/clinical/prescription-fulfillment";

export type FulfillmentStep = "stock" | "billing" | "delivery";

export const FULFILLMENT_STEPS: Array<{
  id: FulfillmentStep;
  label: string;
  hint: string;
  help: string;
}> = [
  {
    id: "stock",
    label: "Stock",
    hint: "Vérifier et réserver",
    help: "Cochez les lignes puis validez le stock — les ruptures peuvent être imprimées pour achat externe.",
  },
  {
    id: "billing",
    label: "Comptabilité",
    hint: "Facturation partielle",
    help: "Envoyez les lignes réservées à la comptabilité (patient ou assureur). Assuré : validation préalable requise.",
  },
  {
    id: "delivery",
    label: "Livraison",
    hint: "Sortie stock & remise",
    help: "Après paiement patient (caisse) ou facture assureur émise — livrez et décrémentez le stock ISC.",
  },
];

function isActiveLine(line: Prescription["lines"][number]): boolean {
  return (
    line.fulfillment.dispenseState !== "delivered" &&
    line.fulfillment.dispenseState !== "external"
  );
}

export function resolveFulfillmentStep(prescription: Prescription): FulfillmentStep {
  const lines = prescription.lines.filter(isActiveLine);
  if (lines.length === 0) return "delivery";

  if (lines.some((l) => l.fulfillment.stockState === "unchecked")) {
    return "stock";
  }

  if (
    lines.some(
      (l) =>
        l.fulfillment.stockState === "reserved" && !l.fulfillment.billingDocumentId,
    )
  ) {
    return "billing";
  }

  if (
    lines.some(
      (l) =>
        l.fulfillment.billingDocumentId &&
        l.fulfillment.dispenseState !== "delivered",
    )
  ) {
    return "delivery";
  }

  if (lines.some((l) => canTransferLineToBilling(prescription.id, l.id).ok)) {
    return "billing";
  }

  if (lines.some((l) => canDeliverLine(prescription.id, l.id).ok)) {
    return "delivery";
  }

  return "stock";
}

export function stepCompletion(
  prescription: Prescription,
  step: FulfillmentStep,
): "pending" | "active" | "done" {
  const order: FulfillmentStep[] = ["stock", "billing", "delivery"];
  const current = resolveFulfillmentStep(prescription);
  const currentIdx = order.indexOf(current);
  const stepIdx = order.indexOf(step);

  if (prescription.lines.filter(isActiveLine).length === 0) return "done";
  if (stepIdx < currentIdx) return "done";
  if (stepIdx === currentIdx) return "active";
  return "pending";
}

export function countLinesForStep(
  prescription: Prescription,
  step: FulfillmentStep,
): number {
  return prescription.lines.filter((line) => lineMatchesStep(prescription, line, step)).length;
}

export function lineMatchesStep(
  _prescription: Prescription,
  line: Prescription["lines"][number],
  step: FulfillmentStep,
): boolean {
  if (step === "stock") {
    return line.fulfillment.stockState === "unchecked";
  }
  if (step === "billing") {
    return (
      line.fulfillment.stockState === "reserved" &&
      !line.fulfillment.billingDocumentId &&
      line.fulfillment.dispenseState !== "delivered"
    );
  }
  return (
    !!line.fulfillment.billingDocumentId &&
    line.fulfillment.dispenseState !== "delivered" &&
    line.fulfillment.dispenseState !== "external"
  );
}

export function getStepHelp(step: FulfillmentStep): string {
  return FULFILLMENT_STEPS.find((s) => s.id === step)?.help ?? "";
}
